import React, { useEffect, useRef, useState } from 'react';

/**
 * Bluesky oEmbed post embed.
 *
 * Fetches the official oEmbed snippet for a post from embed.bsky.app and
 * renders it inline. The snippet is a <blockquote data-bluesky-uri> plus a
 * <script> that loads embed.bsky.app/static/embed.js. Because script tags
 * injected via dangerouslySetInnerHTML do not execute, we load embed.js
 * ourselves and call window.bluesky.scan() on our container to convert the
 * blockquote into the live embed iframe.
 */

let embedScriptPromise = null;

function loadEmbedScript() {
  if (typeof window === 'undefined' || window.bluesky?.scan) return Promise.resolve();

  if (embedScriptPromise) return embedScriptPromise;
  embedScriptPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://embed.bsky.app/static/embed.js';
    script.async = true;
    script.charset = 'utf-8';
    // Resolve on both load and error so scan() still runs (it just finds no
    // embeds if the script failed, leaving the static blockquote fallback).
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return embedScriptPromise;
}

export default function BlueskyPostEmbed({ did, rkey, className = '' }) {
  const containerRef = useRef(null);
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);

  const postUrl = `https://bsky.app/profile/${did}/post/${rkey}`;

  // Fetch the oEmbed snippet for this post.
  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);

    const params = new URLSearchParams({ url: postUrl, format: 'json' });
    fetch(`https://embed.bsky.app/oembed?${params.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Embed failed (HTTP ${res.status})`);

        return res.json();
      })
      .then((data) => {
        if (cancelled) return;

        if (!data.html) throw new Error('No embed HTML returned');
        setHtml(data.html);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load embed');
      });

    return () => {
      cancelled = true;
    };
  }, [postUrl]);

  // Once the blockquote is in the DOM, run embed.js scan() on our container
  // so the static fallback is replaced with the live embed iframe.
  useEffect(() => {
    if (!html || !containerRef.current) return;
    const el = containerRef.current;

    const render = () => {
      try {
        window.bluesky?.scan?.(el);
      } catch {
        // ignore — the static blockquote fallback remains
      }
    };

    if (window.bluesky?.scan) {
      render();
    } else {
      loadEmbedScript().then(render);
    }
  }, [html]);

  if (error) {
    return (
      <a
        href={postUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="altifier-post-embed-link"
      >
        View on Bluesky
      </a>
    );
  }

  if (!html) {
    return <div className="altifier-post-embed-loading">Loading post embed...</div>;
  }

  return (
    <div
      ref={containerRef}
      className={`altifier-post-embed ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
